import { TextFieldModule } from '@angular/cdk/text-field';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { provideProtoTransport } from '@vendasta/protos';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { LexiconModule } from '@galaxy/lexicon';
import { GalaxyI18NModule } from '@vendasta/galaxy/i18n';
import { MeetingAnalysisI18nModule, MEETING_ANALYSIS_ROUTE_PREFIX$ } from '@galaxy/meeting-analysis/static';
import { of } from 'rxjs';
import { SharedModule } from './shared/shared.module';
import { RECAPTCHA_V3_SITE_KEY, RecaptchaV3Module } from 'ng-recaptcha';
import En from '../assets/i18n/en.json';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BookingUrlPipe } from './booking-url.pipe';
import { BookmeBannerComponent } from './bookme-banner/bookme-banner.component';
import { TooltipDirective } from './bookme-banner/tooltip.directive';
import { BookmeBookComponent } from './bookme-book/bookme-book.component';
import { BookmeCalendarComponent } from './bookme-calendar/bookme-calendar.component';
import { MeetingInfoComponent } from './bookme-calendar/meeting-info';
import { ExpandableTextComponent } from './bookme-calendar/meeting-info/expandable-text/expandable-text.component';
import { BookmeCancelComponent } from './bookme-cancel/bookme-cancel.component';
import { BookmeConfirmationComponent } from './bookme-confirmation/bookme-confirmation.component';
import { BookmeContactFormComponent } from './bookme-contact-form/bookme-contact-form.component';
import { BookmeLandingPageComponent } from './bookme-landing-page/bookme-landing-page.component';
import { BookmeMeetingTypeSelectionPageComponent } from './bookme-meeting-type-selection-page/bookme-meeting-type-selection-page.component';
import { BookmeRescheduleComponent } from './bookme-reschedule/bookme-reschedule.component';
import { BookmeRescheduleConfirmationComponent } from './bookme-reschedule/confirmation/confirmation.component';
import { RescheduleConfirmChangesComponent } from './bookme-reschedule/reschedule-confirm-changes/reschedule-confirm-changes.component';
import { RescheduleSuccessComponent } from './bookme-reschedule/reschedule-success/reschedule-success.component';
import { BookmeComponent } from './bookme/bookme.component';
import { DynamicManifestJSONService } from './dynamic-manifest.json';
import { GroupSelectionPageComponent } from './group-selection-page/group-selection-page.component';
import { MeetingInfoPageComponent } from './meeting-info/meeting-info.component';
import { ServiceSelectionPageComponent } from './service-selection-page/service-selection-page.component';
import { CancelInfoPageComponent } from './cancel-info/cancel-info.component';
import { CancelConfirmationPageComponent } from './cancel-confirmation/cancel-confirmation.component';
import { TitleHeaderComponent } from './title-header.component';
import { SchedulingPageComponent } from './scheduling-page/scheduling-page.component';
import { ProviderSelectionPageComponent } from './provider-selection-page/provider-selection-page.component';
import { DatetimeSelectionPageComponent } from './datetime-selection-page/datetime-selection-page.component';
import { ConfirmBookingPageComponent } from './confirm-booking-page/confirm-booking-page.component';
import { BookingLayoutComponent } from './booking-layout/booking-layout.component';
import { GroupServicesDialogComponent } from './scheduling-page/group-services-dialog/group-services-dialog.component';
import { RemoveServiceDialogComponent } from './scheduling-page/remove-service-dialog/remove-service-dialog.component';
import { BookingErrorComponent } from './shared/booking-error/booking-error.component';
import { LocalizeBootstrapService } from './localize-bootstrap.service';

@NgModule({
  declarations: [
    AppComponent,
    BookmeComponent,
    BookmeCalendarComponent,
    BookmeConfirmationComponent,
    BookmeContactFormComponent,
    BookmeLandingPageComponent,
    MeetingInfoComponent,
    BookmeBookComponent,
    BookmeRescheduleComponent,
    BookmeCancelComponent,
    BookmeRescheduleConfirmationComponent,
    RescheduleConfirmChangesComponent,
    RescheduleSuccessComponent,
    BookmeMeetingTypeSelectionPageComponent,
    BookmeBannerComponent,
    BookingUrlPipe,
    ExpandableTextComponent,
    TitleHeaderComponent, // Ensure TitleHeaderComponent is included
    GroupSelectionPageComponent,
    MeetingInfoPageComponent,
    ServiceSelectionPageComponent,
    CancelInfoPageComponent,
    CancelConfirmationPageComponent,
    SchedulingPageComponent,
    ProviderSelectionPageComponent,
    DatetimeSelectionPageComponent,
    ConfirmBookingPageComponent,
    BookingLayoutComponent,
    GroupServicesDialogComponent,
    RemoveServiceDialogComponent,
    BookingErrorComponent,
    TooltipDirective,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    SharedModule,
    AppRoutingModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    TextFieldModule,
    MatSelectModule,
    MatAutocompleteModule,
    GalaxyBadgeModule,
    LexiconModule.forRoot({
      componentName: 'meeting-scheduler/bookme-client',
      baseTranslation: En,
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useValue: {
          handle(params: MissingTranslationHandlerParams): string {
            if (params.key.endsWith(' *')) {
              return params.key.substring(0, params.key.length - 2);
            }
            return params.key;
          },
        },
      },
    }),
    MatCheckboxModule,
    MatChipsModule,
    MatCardModule,
    RecaptchaV3Module,
    MatTooltipModule,
    MatSnackBarModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    GalaxyI18NModule,
    MeetingAnalysisI18nModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [DynamicManifestJSONService],
      useFactory:
        (dynamicManifestJSONService: DynamicManifestJSONService) => () =>
          dynamicManifestJSONService.initialize(),
    },
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [LocalizeBootstrapService],
      useFactory: (svc: LocalizeBootstrapService) => () => svc.init(),
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline' },
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useFactory: getRecaptchaSiteKey,
    },
    provideHttpClient(withInterceptorsFromDi()),
    provideProtoTransport(),
    { provide: MEETING_ANALYSIS_ROUTE_PREFIX$, useValue: of('') },
  ],
})
export class AppModule {}

function getRecaptchaSiteKey(): string {
  const env = readWindowEnvironment();
  if (env === 'PROD') {
    return '6Lfyo94ZAAAAAMSiIHxscnLbXkCfJ5rzwMZJr4Mx';
  }
  if (env === 'DEMO') {
    return '6LecDpAqAAAAAL1OdXI7WTjOfjv3A_ZOFW-uaFRE';
  }
  return '';
}

function readWindowEnvironment(): string {
  const envValue =
    typeof window !== 'undefined'
      ? ((window as unknown as Record<string, unknown>).environment as
          | string
          | undefined)
      : undefined;
  return (envValue ?? '').toUpperCase();
}
